import { DEFAULT_RANKINGS, RANK_BY_FILTER_OPTIONS } from '@/modules/apr/rankings/constants';
import { RANKINGS_TYPE_OPTIONS, WORLD_PICKLEBALL_RANKINGS } from '@/modules/rankings/constants';
import {
  CsvExportPlayerRankingsParams,
  CsvExportTournamentRankingsParams,
} from '@/types/aprRankings.types';

export const getWeeksInLastYear = () => {
  const weeks = [];
  const today = new Date();
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  // Helper to format date as YYYY-MM-DD
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to get Monday of the week
  const getMonday = (date: Date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    return new Date(date.setDate(diff));
  };

  // Start from today's Monday
  const currentDate = getMonday(new Date(today));

  // Get all Mondays until one year ago in reverse order
  while (currentDate >= oneYearAgo) {
    const dateStr = formatDate(currentDate);
    weeks.push({
      id: dateStr,
      title: dateStr,
    });
    currentDate.setDate(currentDate.getDate() - 7);
  }

  return weeks;
};

export const downloadCsv = (csvString: string, fileName: string) => {
  const blob = new Blob([csvString], {
    type: 'text/csv;charset=utf-8;',
  });

  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const getPlayerRankingsFileName = (params: CsvExportPlayerRankingsParams) => {
  const firstWeek = getWeeksInLastYear();
  const parts = [
    'apr_player_rankings',
    params.rankings || DEFAULT_RANKINGS,
    params.rankBy || RANK_BY_FILTER_OPTIONS[0].value,
    `${firstWeek[0]?.id}`,
  ];

  return `${parts.join('_')}.csv`;
};

export const getPartnerRankingsFileName = (exportParams: { params: string }) => {
  const p = new URLSearchParams(exportParams.params);
  const firstWeek = getWeeksInLastYear();
  const week = p.get('week') || `${firstWeek[0]?.id}`;
  // ranking_type defaults to proWorldRanking (World Pickleball Rankings) when not set.
  const rankingTypeLabel =
    RANKINGS_TYPE_OPTIONS.find((option) => option.value === p.get('ranking_type'))?.label ??
    WORLD_PICKLEBALL_RANKINGS;

  return `Rankings Total Records ${rankingTypeLabel} ${week}.csv`;
};

export const getTournamentRankingsFileName = (params: CsvExportTournamentRankingsParams) => {
  const parts = ['apr_tournament_rankings', params.year || 'past_one_year'];

  return `${parts.join('_')}.csv`;
};

export function formatValueToNDecimals(
  value: number | null | undefined,
  noOfDecimals: number,
  placeholder = '--'
) {
  if (!value) {
    return placeholder;
  }

  if (noOfDecimals % 1 !== 0 || noOfDecimals < 0) {
    return value;
  }

  if (value % 1 === 0) {
    return value;
  } else {
    return parseFloat(value.toFixed(noOfDecimals));
  }
}
